import type { PointerEvent as ReactPointerEvent } from 'react';
import type { ResolvedColumn, SortState } from '../../domain';
import { Icon, type IconName } from '../shared/Icon';
import { columnStyle } from './Body';
import { GroupRow } from './GroupRow';

export interface HeadProps<T> {
    columns: ResolvedColumn<T>[];
    sort: SortState | null;
    onSort: (col: ResolvedColumn<T>) => void;
    onResizeStart?: (
        event: ReactPointerEvent<HTMLElement>,
        col: ResolvedColumn<T>,
    ) => void;
    leading?: JSX.Element[];
    trailing?: JSX.Element[];
    collapsibleGroups?: Set<string>;
    isGroupCollapsed?: (key: string) => boolean;
    onToggleGroup?: (key: string) => void;
}

type AriaSort = 'ascending' | 'descending' | 'none';

const SORT_ICON: Record<AriaSort, IconName> = {
    ascending: 'arrow_upward',
    descending: 'arrow_downward',
    none: 'swap_vert',
};

function ariaSortFor<T>(
    sort: SortState | null,
    col: ResolvedColumn<T>,
): AriaSort {
    const key = col.sortKey ?? col.id;
    if (!sort || sort.key !== key) {
        return 'none';
    }
    return sort.dir === 'asc' ? 'ascending' : 'descending';
}

interface HeadCellProps<T> {
    col: ResolvedColumn<T>;
    sort: SortState | null;
    onSort: (col: ResolvedColumn<T>) => void;
    onResizeStart?: (
        event: ReactPointerEvent<HTMLElement>,
        col: ResolvedColumn<T>,
    ) => void;
}

function HeadCell<T>(props: HeadCellProps<T>): JSX.Element {
    const { col, sort, onSort, onResizeStart } = props;
    const ariaSort = ariaSortFor(sort, col);

    return (
        <th
            className="ics-table__head-cell"
            style={columnStyle(col)}
            aria-sort={col.canSort ? ariaSort : undefined}
        >
            {col.canSort ? (
                <button
                    type="button"
                    className="ics-table__sort-button"
                    onClick={() => {
                        onSort(col);
                    }}
                >
                    {col.header}
                    <span className="ics-table__sort-icon">
                        <Icon name={SORT_ICON[ariaSort]} />
                    </span>
                </button>
            ) : (
                col.header
            )}
            {onResizeStart ? (
                <span
                    role="presentation"
                    className="ics-table__resize-handle"
                    aria-label={`Resize ${col.header}`}
                    onPointerDown={(event) => {
                        onResizeStart(event, col);
                    }}
                />
            ) : null}
        </th>
    );
}

export function Head<T>(props: HeadProps<T>): JSX.Element {
    const {
        columns,
        sort,
        onSort,
        onResizeStart,
        leading = [],
        trailing = [],
    } = props;
    const { collapsibleGroups, isGroupCollapsed, onToggleGroup } = props;
    const hasGroups = columns.some((col) => col.groupHeader !== undefined);

    return (
        <thead>
            {hasGroups ? (
                <GroupRow
                    columns={columns}
                    leading={leading}
                    trailing={trailing}
                    collapsibleGroups={collapsibleGroups}
                    isGroupCollapsed={isGroupCollapsed}
                    onToggleGroup={onToggleGroup}
                />
            ) : null}
            <tr>
                {leading}
                {columns.map((col) => (
                    <HeadCell
                        key={col.id}
                        col={col}
                        sort={sort}
                        onSort={onSort}
                        onResizeStart={onResizeStart}
                    />
                ))}
                {trailing}
            </tr>
        </thead>
    );
}
